import {ActionCreator, createAction} from '@packages/utilities/create-action';
import {
  EXTENSION_DEV_LAUNCH_BY_DEFAULT_UPDATE,
  EXTENSION_DEV_MANIFEST_SUCCEEDED,
  EXTENSION_DEV_URL_UPDATE,
  EXTENSION_OPEN,
  EXTENSION_WINDOW_MODE_TOGGLE,
  FETCH_INSTALLED_APPS_SUCCEEDED,
  INSTALL_MARKETPLACE_APP_SUCCEEDED,
  MARKETPLACE_APP_LIKE_REQUESTED,
  MARKETPLACE_APP_LIKE_SUCCEEDED,
  MARKETPLACE_APP_LIKE_FAILED,
  MARKETPLACE_APP_DETAILS_REQUESTED,
  MARKETPLACE_APP_DETAILS_SUCCEEDED,
  MARKETPLACE_APPS_SUCCEEDED,
  MARKETPLACE_APPS_SEARCH_REQUESTED,
  OAUTH_MARKETPLACE_APP_AUTHENTICATED,
  RAW_SITE_DATA_RECEIVED,
  REMOVE_APP_CONNECTION,
  RESET_APP_INSTALL_REDIRECT_URL,
  NOTE_DISMISSED,
  MARKETPLACE_APPS_SEARCH_SUCCEEDED,
  NEW_AND_NOTEWORTHY_APPS_LOAD_REQUESTED,
  NEW_AND_NOTEWORTHY_APPS_LOAD_SUCCEEDED,
} from '@packages/systems/designer/actions';
import {
  AppLaunchLocation,
  ExtensionSize,
  InstalledApp,
  MarketplaceApp,
  AppIntents,
  CurrentAppIntent,
} from '@packages/systems/designer/apps/types';
import {Map} from 'immutable';
import type {CurrentAppConnection} from '@packages/systems/core/records/DesignerState';

export const extensionOpen: ActionCreator<
  typeof EXTENSION_OPEN,
  {
    appId: string;
    dev: boolean;
    location: AppLaunchLocation;
    currentAppConnection?: CurrentAppConnection;
    currentAppIntent?: CurrentAppIntent;
    shouldCreateAppConnection?: boolean;
  }
> = createAction(EXTENSION_OPEN);

export const searchMarketplaceApps: ActionCreator<
  typeof MARKETPLACE_APPS_SEARCH_REQUESTED,
  {
    search: string;
    categories?: string[];
    price?: 'free' | 'paid' | 'any';
    sort?: 'new' | 'popular' | 'default';
    limit?: number;
    offset?: number;
  }
> = createAction(MARKETPLACE_APPS_SEARCH_REQUESTED);

export const marketplaceAppRequestDetails: ActionCreator<
  typeof MARKETPLACE_APP_DETAILS_REQUESTED,
  {
    appSlug: string;
  }
> = createAction(MARKETPLACE_APP_DETAILS_REQUESTED);

export const marketplaceAppOauthAuthenticated: ActionCreator<
  typeof OAUTH_MARKETPLACE_APP_AUTHENTICATED,
  void
> = createAction(OAUTH_MARKETPLACE_APP_AUTHENTICATED);

export type MarketplaceAppLikeMeta = {
  slug: string;
  liked: boolean;
};

export const marketplaceAppLikeToggle: ActionCreator<
  typeof MARKETPLACE_APP_LIKE_REQUESTED,
  MarketplaceAppLikeMeta
> = createAction(MARKETPLACE_APP_LIKE_REQUESTED);

export type MarketplaceAppLikeRequested = {
  type: typeof MARKETPLACE_APP_LIKE_REQUESTED;
  payload: MarketplaceAppLikeMeta;
};

export type MarketplaceAppLikeSucceeded = {
  type: typeof MARKETPLACE_APP_LIKE_SUCCEEDED;
  meta: MarketplaceAppLikeMeta;
};

export type MarketplaceAppLikeFailed = {
  type: typeof MARKETPLACE_APP_LIKE_FAILED;
  meta: MarketplaceAppLikeMeta;
  // The server's JSON error body is {msg, code, name, ...}; a 409 here means the
  // like is already in sync with the server, so the optimistic state must stay.
  response?: {code?: number};
};

export const loadNewAndNoteworthyApps: ActionCreator<
  typeof NEW_AND_NOTEWORTHY_APPS_LOAD_REQUESTED,
  void
> = createAction(NEW_AND_NOTEWORTHY_APPS_LOAD_REQUESTED);

export type GetMarketplaceAppsSuccess = {
  type: typeof MARKETPLACE_APPS_SUCCEEDED;
  payload: {
    response: Array<MarketplaceApp>;
  };
};

export type GetNewAndNoteworthyAppsSuccess = {
  type: typeof NEW_AND_NOTEWORTHY_APPS_LOAD_SUCCEEDED;
  payload: {
    response: Array<MarketplaceApp>;
  };
};

export type GetMarketplaceAppsSearchSuccess = {
  type: typeof MARKETPLACE_APPS_SEARCH_SUCCEEDED;
  payload: {
    response: Array<MarketplaceApp & {id: string}>;
  };
};

export type GetMarketplaceAppDetailsSuccess = {
  type: typeof MARKETPLACE_APP_DETAILS_SUCCEEDED;
  payload: {
    response: MarketplaceApp;
  };
};

export type MarketplaceAppInstallSuccess = {
  type: typeof INSTALL_MARKETPLACE_APP_SUCCEEDED;
  payload: {
    response: string;
  };
};

export type ResetAppInstallRedirectUrlAction = {
  type: typeof RESET_APP_INSTALL_REDIRECT_URL;
};

export type MarketplaceAppOAuthAuthenticated = {
  type: typeof OAUTH_MARKETPLACE_APP_AUTHENTICATED;
  payload: {
    response: string;
  };
};

export type AppsRawSiteDataReceivedAction = {
  type: typeof RAW_SITE_DATA_RECEIVED;
  siteData: {
    installedApps: Array<InstalledApp>;
  };
  state: any;
};

export type FetchInstalledAppsAction = {
  type: typeof FETCH_INSTALLED_APPS_SUCCEEDED;
  payload: {
    response: {
      apps: Array<InstalledApp>;
    };
  };
};

export const extensionDevUrlUpdate: ActionCreator<
  typeof EXTENSION_DEV_URL_UPDATE,
  {
    appId: string;
    url: string;
  }
> = createAction(EXTENSION_DEV_URL_UPDATE);

export const extensionDevLaunchByDefaultUpdate: ActionCreator<
  typeof EXTENSION_DEV_LAUNCH_BY_DEFAULT_UPDATE,
  {
    appId: string;
    checked: boolean;
  }
> = createAction(EXTENSION_DEV_LAUNCH_BY_DEFAULT_UPDATE);

export type ExtensionDevManifestSucceeded = {
  type: typeof EXTENSION_DEV_MANIFEST_SUCCEEDED;
  payload: {
    response: {
      apiVersion?: '1' | '2';
      size: ExtensionSize;
      featureFlags?: {[key: string]: boolean};
      appConnections?: Array<string>;
      appIntents?: AppIntents;
    };
  };
  meta: Map<string, string>;
};

export const toggleExtensionWindowMode: ActionCreator<
  typeof EXTENSION_WINDOW_MODE_TOGGLE,
  {
    minimized: boolean;
  }
> = createAction(EXTENSION_WINDOW_MODE_TOGGLE);

export const removeAppConnection: ActionCreator<
  typeof REMOVE_APP_CONNECTION,
  {
    appId: string;
  }
> = createAction(REMOVE_APP_CONNECTION);

export const dismissNote: ActionCreator<
  typeof NOTE_DISMISSED,
  {
    id: string;
  }
> = createAction(NOTE_DISMISSED);
